const BREAKPOINT_DESKTOP = 769;
const GAP                = 12;
const PER_VIEW_DESKTOP   = 6;
const PER_VIEW_MOBILE    = 5;

class PdpVariantPicker extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._buildSwiper();
  }

  disconnectedCallback() {
    if (this._swiper) {
      try { this._swiper.destroy(true, true); } catch (e) {}
      this._swiper = null;
    }
  }

  _buildSwiper() {
    const wrapper = this.querySelector('.swiper-wrapper');
    if (!wrapper) return;
    const slides = wrapper.querySelectorAll('.swiper-slide');
    if (!slides.length) return;

    const scope     = this.closest('.option-selector') || this.parentNode;
    const prevEl    = scope.querySelector('[data-pdp-vp-prev]');
    const nextEl    = scope.querySelector('[data-pdp-vp-next]');
    const counterEl = scope.querySelector('[data-pdp-vp-counter]');
    const navWrap   = scope.querySelector('[data-pdp-vp-nav]');

    const initialIdx = this._activeSlideIndex(slides);

    this._swiper = new window.Swiper(this, {
      slidesPerView:  PER_VIEW_MOBILE,
      slidesPerGroup: PER_VIEW_MOBILE,
      spaceBetween:   GAP,
      watchOverflow:  true,
      observer:       true,
      observeParents: true,
      initialSlide:   initialIdx,
      breakpoints: {
        [BREAKPOINT_DESKTOP]: {
          slidesPerView:  PER_VIEW_DESKTOP,
          slidesPerGroup: PER_VIEW_DESKTOP
        }
      },
      navigation: {
        prevEl,
        nextEl,
        disabledClass: 'swiper-button-disabled',
        lockClass:     'swiper-button-lock'
      },
      on: {
        init:        () => this._syncNav(navWrap, counterEl),
        slideChange: () => this._syncNav(navWrap, counterEl),
        breakpoint:  () => this._syncNav(navWrap, counterEl),
        resize:      () => this._syncNav(navWrap, counterEl),
        lock:        () => this._syncNav(navWrap, counterEl),
        unlock:      () => this._syncNav(navWrap, counterEl)
      }
    });

    // When the user picks a radio that lives off-screen, scroll the minimum
    // amount to bring its slide into the visible window.
    this.addEventListener('change', (e) => {
      if (!e.target?.classList?.contains('opt-btn')) return;
      if (!this._swiper) return;
      const idx     = this._activeSlideIndex(slides);
      const sw      = this._swiper;
      const perView = sw.params.slidesPerView;
      const active  = sw.activeIndex;
      if (idx < active) {
        sw.slideTo(idx);
      } else if (idx >= active + perView) {
        sw.slideTo(Math.min(idx - perView + 1, slides.length - perView));
      }
    });
  }

  _activeSlideIndex(slides) {
    for (let i = 0; i < slides.length; i++) {
      const input = slides[i].querySelector('.opt-btn');
      if (input?.checked) return i;
    }
    return 0;
  }

  _syncNav(navWrap, counterEl) {
    if (!this._swiper) return;
    const sw = this._swiper;
    // Use Swiper's snapGrid: it's the list of real stop positions the next
    // button steps through, so snapIndex/snapGrid.length match the actual
    // group count even when the last group is partial.
    const total   = Math.max(1, (sw.snapGrid && sw.snapGrid.length) || 1);
    const current = Math.min(total, (sw.snapIndex || 0) + 1);
    const locked  = sw.isLocked === true;

    if (counterEl) counterEl.textContent = locked ? '' : `${current}/${total}`;
    if (navWrap)   navWrap.classList.toggle('pdp-variant-picker-nav--enabled', !locked);
  }
}

if (!customElements.get('pdp-variant-picker')) {
  customElements.define('pdp-variant-picker', PdpVariantPicker);
}

// Decides which variant a PDP opens on, and writes that decision into the
// pickers on every load - not just the first one.
//
// ?variant= in the URL wins: Liquid already renders it checked, but a reload
// lets the browser restore the radios it remembers from before the reload, which
// can leave the size looking unselected. Re-checking them here makes the buttons
// match the URL whatever the browser restored.
//
// With no ?variant=, open on the first size the shopper can actually buy.
// Shopify's selected_or_first_available_variant walks the variant list instead,
// so on a sparse matrix it lands on a late size: men-linen-classic-shorts opens
// on XL because BEIGE is sold out below it, while S is in stock in WHITE.
function variantForUrl(product) {
  const id = new URLSearchParams(location.search).get('variant');
  return id && product.variants.find((v) => String(v.id) === id);
}

function variantForFirstAvailableSize(product, optionSelectors) {
  // Option names come from the product data, not the label markup: names like
  // "Junior Size (Years)" don't survive the vart-{{ option.name }} class.
  const sizeSelector = Array.from(
    optionSelectors.querySelectorAll('.option-selector[data-selector-type="listed"]')
  ).find((el) => /size/i.test(product.options[Number(el.dataset.optionIndex)] || ''));
  if (!sizeSelector) return null;

  const index = Number(sizeSelector.dataset.optionIndex);
  // Radio order, not variant order - that is the list the shopper reads.
  for (const radio of sizeSelector.querySelectorAll('.opt-btn')) {
    const match = product.variants.find((v) => v.available && v.options[index] === radio.value);
    if (match) return match;
  }
  return null;
}

function applyVariantToPickers(optionSelectors) {
  const form = optionSelectors.closest('.product-form');
  const json = form && document.getElementById('ProductJson-' + form.dataset.productId);
  if (!json) return;

  const product = JSON.parse(json.innerHTML);
  const variant = variantForUrl(product) || variantForFirstAvailableSize(product, optionSelectors);
  if (!variant) return;

  let changed = false;
  let lastRadio = null;

  optionSelectors.querySelectorAll('.option-selector').forEach((selector) => {
    const value = variant.options[Number(selector.dataset.optionIndex)];
    if (value === undefined) return;

    if (selector.dataset.selectorType === 'dropdown') {
      const select = selector.querySelector('.cc-select');
      if (select) select.dispatchEvent(new CustomEvent('selectOption', { detail: { value } }));
      return;
    }

    selector.querySelectorAll('.opt-btn').forEach((radio) => {
      const wanted = radio.value === value;
      if (radio.checked !== wanted) {
        radio.checked = wanted;
        changed = true;
      }
      if (wanted) lastRadio = radio;
    });

    const label = selector.querySelector('.selected-color-option');
    if (label) label.textContent = value;
  });

  // Only nudge the theme when the pickers actually moved - it resolves the
  // variant from the checked radios and updates price, gallery, id and URL.
  if (changed && lastRadio) {
    lastRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// After load, so the theme has bound its option-selector handlers and any
// browser form restoration has already happened.
window.addEventListener('load', () => {
  document
    .querySelectorAll('.product-detail .option-selectors')
    .forEach(applyVariantToPickers);
});

// No-reload colour switching replaces the product section after `load` has
// already fired, so without this the new product never gets its first
// available size and opens with none chosen - which the size guard in
// theme.liquid then treats as "pick a size", refusing add to cart.
// pdp-variant-swap.js emits this once the new section is fully initialised,
// so the change dispatched here reaches bound handlers and writes a real
// variant id into the form.
document.addEventListener('pdp:variant-swapped', (e) => {
  e.target
    .querySelectorAll('.product-detail .option-selectors')
    .forEach(applyVariantToPickers);
});
